/**
 * Every copy decision the Quote Activity log and the Baseline lock banner make, as pure
 * functions. Kept out of the components so every permutation can be unit-tested without
 * rendering anything.
 */

/** Sentinel the caller replaces with the localised "Deleted user" label. */
export const DELETED_USER = '__DELETED_USER__';

export type QuoteActivityEntry = {
	eventType: string | null;
	/** The quote revision the event belongs to. Null is treated as the first. */
	revisionNumber?: number | null;
	personName: string | null;
	clientContactName: string | null;
	reason: string | null;
};

/**
 * One rendered log entry: a bold event title, the acting user, and — only where the event can
 * carry one — the contact the decision was recorded for.
 */
export type ActivityCopy = {
	titleMessageId: string;
	person: string;
	contact: string | null;
};

/**
 * The event title, and whether that event can name a contact. PUBLISHED and REVISING never can —
 * neither a publish nor a revision is made on anyone's behalf — so their contact is dropped even
 * if the row somehow carries one.
 */
const EVENT_TITLES: {[eventType: string]: {titleMessageId: string; carriesContact: boolean}} = {
	PUBLISHED: {titleMessageId: 'quote_activity.entry.published', carriesContact: false},
	SENT: {titleMessageId: 'quote_activity.entry.sent', carriesContact: false},
	APPROVED: {titleMessageId: 'quote_activity.entry.approved', carriesContact: true},
	DECLINED: {titleMessageId: 'quote_activity.entry.declined', carriesContact: true},
	REVISING: {titleMessageId: 'quote_activity.entry.revising', carriesContact: false},
};

/**
 * A republish after a revision writes the SAME PUBLISHED event as the very first publish, so the
 * event type alone would render both as "Quote created" and the log would claim a project created
 * its quote twice. The revision is what separates them, which is why the graph exposes it on the
 * activity row rather than the client inferring it from position in the list — the log is capped at
 * `first: 100`, so an entry's neighbours are not a reliable witness to what came before it.
 */
const REPUBLISHED_TITLE_ID = 'quote_activity.entry.republished';

/**
 * Every status the Baseline stays locked in. REVISING is deliberately absent — revising a quote
 * unlocks the Baseline again. Mirrors the constant InitialPlanPage derives its lock from.
 */
export const LOCKED_QUOTE_STATUSES: readonly string[] = ['PUBLISHED', 'SENT', 'APPROVED', 'DECLINED'];

/**
 * Every status in which an ACTIVE quote exists: View and Send are offered, Create Quote and the
 * designer route are not.
 *
 * Identical in contents to LOCKED_QUOTE_STATUSES today, and deliberately a separate constant: the
 * two answer different questions — may the Baseline be edited, versus does an active quote exist —
 * and the expiry work is the likely place they diverge.
 */
export const LIVE_QUOTE_STATUSES: readonly string[] = ['PUBLISHED', 'SENT', 'APPROVED', 'DECLINED'];

/**
 * The client mirror of QuoteDefaults.DECISION_TRANSITIONS' allowedFrom. One table shaped like the
 * server's, so the greyed-out button and the server's rejection cannot disagree.
 *
 * REVISING's absence from its own row is what enforces revise-once-per-revision — the same single
 * omission the Java table makes.
 */
export const DECISION_FROM_STATUSES: {[decision: string]: readonly string[]} = {
	APPROVED: ['PUBLISHED', 'SENT'],
	DECLINED: ['PUBLISHED', 'SENT'],
	REVISING: ['PUBLISHED', 'SENT', 'APPROVED', 'DECLINED'],
};

const BANNER_KEYS: {[status: string]: string} = {
	PUBLISHED: 'baseline.locked_banner_published',
	SENT: 'baseline.locked_banner_sent',
	APPROVED: 'baseline.locked_banner_approved',
	DECLINED: 'baseline.locked_banner_declined',
	REVISING: 'baseline.unlocked_banner_revising',
};

/** True while an active quote exists. A revising quote and no quote at all both answer false. */
export function isLiveQuote(status: string | null | undefined): boolean {
	return !!status && LIVE_QUOTE_STATUSES.indexOf(status) !== -1;
}

/** May this decision be recorded against a quote in this status? False for an unknown decision. */
export function canRecord(decision: string, status: string | null | undefined): boolean {
	if (!status) return false;
	const allowedFrom = DECISION_FROM_STATUSES[decision];
	return !!allowedFrom && allowedFrom.indexOf(status) !== -1;
}

/**
 * The three contact states, told apart by the snapshot column alone:
 *   id set + name set   → the contact exists          → "On behalf of Jane Doe"
 *   id null + name set  → deleted after the decision   → the SAME line, from the snapshot
 *   id null + name null → no contact was ever recorded → no on-behalf-of line at all
 *
 * The first two are indistinguishable here on purpose: only the name matters for display, which
 * is why this takes clientContactName and not the id.
 *
 * Returns null for an unknown event type rather than rendering a broken entry: later lifecycle
 * work adds event types (expiry, revision) that this build does not know about.
 */
/**
 * One email-correspondence entry, rendered nested under the timeline rather than as a timeline
 * event of its own: who wrote it, which side they are on, how it travelled, and the message text.
 */
export type ReplyCopy = {
	name: string;
	tagMessageId: string;
	bylineMessageId: string;
	body: string | null;
	isClient: boolean;
};

/** A reply RECEIVED from a client contact. Written by inbound-mail capture (or seeded); never by this client. */
const EVENT_CLIENT_REPLIED = 'CLIENT_REPLIED';
/** A reply the acting user emailed to a client contact. */
const EVENT_REPLY_SENT = 'REPLY_SENT';

export function isReplyEvent(eventType: string | null): boolean {
	return eventType === EVENT_CLIENT_REPLIED || eventType === EVENT_REPLY_SENT;
}

/**
 * A client reply reads from the CONTACT snapshot, an outbound one from the acting person — each
 * falls back to the deleted-user sentinel so a scrubbed author still renders as an entry rather
 * than vanishing from the correspondence.
 */
export function describeReply(entry: QuoteActivityEntry): ReplyCopy | null {
	if (!isReplyEvent(entry.eventType)) return null;
	const isClient = entry.eventType === EVENT_CLIENT_REPLIED;
	const author = isClient ? entry.clientContactName : entry.personName;
	return {
		name: author && author.trim() ? author : DELETED_USER,
		tagMessageId: isClient ? 'quote_activity.client_tag' : 'quote_activity.you_tag',
		bylineMessageId: isClient ? 'quote_activity.replied_by_email' : 'quote_activity.sent_by_email',
		body: entry.reason,
		isClient,
	};
}

export function describeActivity(entry: QuoteActivityEntry): ActivityCopy | null {
	if (!entry.eventType) return null;
	const event = EVENT_TITLES[entry.eventType];
	if (!event) return null;

	const isRepublish = entry.eventType === 'PUBLISHED' && (entry.revisionNumber || 1) > 1;
	const person = entry.personName && entry.personName.trim() ? entry.personName : DELETED_USER;
	const snapshot = entry.clientContactName && entry.clientContactName.trim() ? entry.clientContactName : null;

	return {
		titleMessageId: isRepublish ? REPUBLISHED_TITLE_ID : event.titleMessageId,
		person,
		contact: event.carriesContact ? snapshot : null,
	};
}

export function bannerMessageId(status: string | null): string | null {
	if (!status) return null;
	return BANNER_KEYS[status] || null;
}
